import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  heaterWallBands,
  isPolyRoom,
  polyBenchBands,
  polyEdges,
  polyInnerBenchBands,
  polyInnerWallBand,
  wallView,
} from './polyRoom';

/**
 * Flush-to-wall snapping for objects dragged inside a room.
 *
 * The move drag's existing snap is axis-aligned point matching, which can align an
 * object to a wall only when that wall is horizontal or vertical. This module snaps
 * an object FLUSH against the face of any wall at any angle: the object is rotated
 * parallel to the wall, then translated along the wall's normal until its back face
 * sits on the wall face.
 *
 * Faces come from every surface that bounds the room interior — the outer walls'
 * inner faces, both faces of each interior partition, the room-facing face of each
 * hidden heater wall, and the bench bands — so an object butts against whichever it
 * is nearest, exactly as it would in the real room.
 *
 * Pure geometry: no DOM, no app state, so it is unit-tested directly.
 */

/** A snappable surface: a segment plus the unit normal pointing away from the
 *  solid it bounds (i.e. into the space an object can occupy). */
export interface SnapFace {
  a: Vec2;
  b: Vec2;
  /** Unit normal pointing into the free space in front of the face. */
  n: Vec2;
}

export interface RoomSnap {
  /** Translation to apply to the instance. */
  dx: number;
  dy: number;
  /** Rotation (degrees) the object should take to lie parallel to the face. */
  rotation: number;
  /** The face snapped to, for a highlight/guide. */
  face: SnapFace;
  /** Perpendicular distance that was closed (mm). */
  gap: number;
}

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const len = (a: Vec2): number => Math.hypot(a.x, a.y);

/** Rotate a local point by `deg` (CCW) — mirrors geometry.rotatePoint. */
function rot(p: Vec2, deg: number): Vec2 {
  const r = (deg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

/** World-space footprint corners of an instance at an assumed rotation. */
export function footprintAt(inst: Pick<ProductInstance, 'x' | 'y' | 'width' | 'depth'>, rotation: number): Vec2[] {
  const hw = inst.width / 2, hd = inst.depth / 2;
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }]
    .map((p) => rot(p, rotation))
    .map((p) => ({ x: p.x + inst.x, y: p.y + inst.y }));
}

/** A box in the object's own frame (mm, centre at the origin, unrotated). */
export interface LocalBox { x0: number; y0: number; x1: number; y1: number }

/** World-space corners of a local box at an assumed rotation. */
export function boxAt(inst: Pick<ProductInstance, 'x' | 'y'>, box: LocalBox, rotation: number): Vec2[] {
  return [{ x: box.x0, y: box.y0 }, { x: box.x1, y: box.y0 }, { x: box.x1, y: box.y1 }, { x: box.x0, y: box.y1 }]
    .map((p) => rot(p, rotation))
    .map((p) => ({ x: p.x + inst.x, y: p.y + inst.y }));
}

/** Every ring edge as a face whose normal points OUTWARD from the ring (away from
 *  the solid it encloses), for a ring wound either way. */
function ringFaces(ring: Vec2[], toWorld: (p: Vec2) => Vec2): SnapFace[] {
  if (ring.length < 3) return [];
  const w = ring.map(toWorld);
  // Signed area decides which side is "outside" for this winding.
  let area = 0;
  for (let i = 0; i < w.length; i++) {
    const p = w[i], q = w[(i + 1) % w.length];
    area += p.x * q.y - q.x * p.y;
  }
  const ccw = area > 0;
  const out: SnapFace[] = [];
  for (let i = 0; i < w.length; i++) {
    const a = w[i], b = w[(i + 1) % w.length];
    const d = sub(b, a), L = len(d);
    if (L < 1e-6) continue;
    const u = { x: d.x / L, y: d.y / L };
    // Outward normal: for a CCW ring the interior is on the left, so outward is right.
    out.push({ a, b, n: ccw ? { x: u.y, y: -u.x } : { x: -u.y, y: u.x } });
  }
  return out;
}

/** The room's walls' inner faces, in world space: the outer walls' and each
 *  hidden heater wall's room-facing face — what a part through a wall passes
 *  through. */
export function roomWallFaces(room: ProductInstance, toWorld: (p: Vec2) => Vec2): SnapFace[] {
  if (!isPolyRoom(room)) return [];
  const faces: SnapFace[] = [];

  // Outer walls: the inner face, normal pointing into the room. `ia→ib` runs along
  // the wall; the room interior lies on the side the edge normal points to.
  for (const e of polyEdges(wallView(room))) {
    const a = toWorld(e.ia), b = toWorld(e.ib);
    if (len(sub(b, a)) < 1e-6) continue;
    // e.normal is the inward normal in local space; rotate it the same way toWorld
    // rotates points (difference of two mapped points cancels the translation).
    const o = toWorld({ x: 0, y: 0 });
    const n = sub(toWorld(e.normal), o);
    const L = len(n) || 1;
    faces.push({ a, b, n: { x: n.x / L, y: n.y / L } });
  }

  // Hidden heater walls: the band's room-facing face (band order is
  // [gapFace a, gapFace b, innerFace b, innerFace a]).
  for (const hb of heaterWallBands(room)) {
    if (hb.poly.length < 4) continue;
    faces.push(...ringFaces(hb.poly, toWorld).filter((_, i) => i === 2));
  }
  return faces;
}

/** The room's outer walls' OUTER faces, in world space, each normal pointing
 *  away from the room — what a part stands against from outside it. */
export function roomOuterWallFaces(room: ProductInstance, toWorld: (p: Vec2) => Vec2): SnapFace[] {
  if (!isPolyRoom(room)) return [];
  const faces: SnapFace[] = [];
  const o = toWorld({ x: 0, y: 0 });
  for (const e of polyEdges(wallView(room))) {
    const a = toWorld(e.a), b = toWorld(e.b);
    if (len(sub(b, a)) < 1e-6) continue;
    const n = sub(toWorld(e.normal), o);
    const L = len(n) || 1;
    faces.push({ a, b, n: { x: -n.x / L, y: -n.y / L } });
  }
  return faces;
}

/**
 * All surfaces inside `room` an object can sit flush against, in world space:
 * its walls' inner faces (`roomWallFaces`), each interior partition's faces and
 * the bench bands.
 */
export function roomSnapFaces(room: ProductInstance, toWorld: (p: Vec2) => Vec2): SnapFace[] {
  if (!isPolyRoom(room)) return [];
  const faces = roomWallFaces(room, toWorld);

  // Interior partitions: every face of the band (an object can sit on either side).
  for (const iw of room.innerWalls || []) {
    if (iw.edge == null) continue;
    faces.push(...ringFaces(polyInnerWallBand(room, iw), toWorld));
  }

  // Benches: an object commonly butts against a bench edge.
  for (const band of polyBenchBands(room)) faces.push(...ringFaces(band.poly, toWorld));
  for (const ib of polyInnerBenchBands(room)) faces.push(...ringFaces(ib.poly, toWorld));

  return faces;
}

/** The rotation nearest `current` that lies parallel to a face (4 orientations). */
export function alignedRotation(current: number, face: SnapFace): number {
  const wall = (Math.atan2(face.b.y - face.a.y, face.b.x - face.a.x) * 180) / Math.PI;
  let best = current, bestD = Infinity;
  for (let k = 0; k < 4; k++) {
    const cand = wall + k * 90;
    // Smallest signed turn from `current` to `cand`, modulo 360.
    const d = Math.abs(((cand - current + 540) % 360) - 180);
    if (d < bestD) { bestD = d; best = ((cand % 360) + 360) % 360; }
  }
  return best;
}

/** The rotation (degrees CCW) that turns an object's local +y to a face's normal,
 *  out of the face into the free space in front of it. */
export function facingRotation(face: SnapFace): number {
  const deg = (Math.atan2(-face.n.x, face.n.y) * 180) / Math.PI;
  return Math.round((((deg % 360) + 360) % 360) * 1e9) / 1e9;
}

/**
 * Snap `inst` flush against the nearest surface of `room` within `tol` mm.
 *
 * `boxes` are the outlines that may sit flush, in the object's own frame — by
 * default just its body; a heater also offers its clearance gap, so either the
 * body or the gap's edge lands on the wall, whichever is nearer.
 *
 * The object is first rotated parallel to the candidate face, then measured: `gap`
 * is how far its nearest corner sits from the face along the face normal (positive
 * = floating in front of it, negative = overlapping into it). The returned
 * translation closes that gap. A face is only considered when the object actually
 * lies alongside it, so an object never snaps to a wall it is nowhere near.
 *
 * `facing` turns it so its local +y points into the room (`facingRotation`) rather
 * than to whichever of the face's four directions is nearest its own, and seats
 * it on the walls alone (`roomWallFaces`): a part that passes through the wall (a
 * steam conveyor) always has its room side inside — a bench's back face lies on
 * the wall's but looks into it, its ends along it.
 *
 * Returns null when nothing is in range — the caller then leaves the drag alone.
 */
export function roomFaceSnap(
  inst: Pick<ProductInstance, 'x' | 'y' | 'width' | 'depth' | 'rotation'>,
  room: ProductInstance,
  toWorld: (p: Vec2) => Vec2,
  tol: number,
  boxes: LocalBox[] = [{ x0: -inst.width / 2, y0: -inst.depth / 2, x1: inst.width / 2, y1: inst.depth / 2 }],
  opts: { facing?: boolean } = {},
): RoomSnap | null {
  return faceSnap(inst, opts.facing ? roomWallFaces(room, toWorld) : roomSnapFaces(room, toWorld), tol, boxes, opts);
}

/** `roomFaceSnap` against any `faces`: the nearest within `tol` mm the object
 *  lies alongside, turned parallel to it (`facing`: its local +y along the
 *  face's normal, out into the free space). */
export function faceSnap(
  inst: Pick<ProductInstance, 'x' | 'y' | 'width' | 'depth' | 'rotation'>,
  faces: readonly SnapFace[],
  tol: number,
  boxes: LocalBox[] = [{ x0: -inst.width / 2, y0: -inst.depth / 2, x1: inst.width / 2, y1: inst.depth / 2 }],
  opts: { facing?: boolean } = {},
): RoomSnap | null {
  let best: RoomSnap | null = null;
  for (const f of faces) for (const box of boxes) {
    const d = sub(f.b, f.a), L = len(d);
    if (L < 1e-6) continue;
    const u = { x: d.x / L, y: d.y / L };
    const rotation = opts.facing ? facingRotation(f) : alignedRotation(inst.rotation, f);
    const corners = boxAt(inst, box, rotation);
    // Distance from the face to the object's nearest corner, along the face normal.
    let gap = Infinity;
    for (const c of corners) gap = Math.min(gap, dot(sub(c, f.a), f.n));
    if (!isFinite(gap) || Math.abs(gap) > tol) continue;
    // Only snap to a face the object is actually beside: its span along the face
    // must overlap the face's own span.
    let lo = Infinity, hi = -Infinity;
    for (const c of corners) {
      const t = dot(sub(c, f.a), u);
      lo = Math.min(lo, t); hi = Math.max(hi, t);
    }
    if (hi <= 0 || lo >= L) continue;
    if (!best || Math.abs(gap) < Math.abs(best.gap)) {
      best = { dx: -f.n.x * gap, dy: -f.n.y * gap, rotation, face: f, gap };
    }
  }
  return best;
}
